import { useEffect, useState } from "react";
import { listAllEvents, type AgendaMark } from "../agenda";
import { listFicheContacts, noteVisit } from "../api";
import { aimedFind } from "../find";
import { Atelier, CoffreCreateButtons, type AtelierStart } from "../Atelier";
import { CoffreBlock } from "../CoffreBlock";
import { FactureBoard } from "../FactureBoard";
import { FactureTab } from "../FactureTab";
import { FicheDesk } from "../FicheDesk";
import { identityLine } from "../ficheIdentity";
import { FicheMark } from "../FicheMark";
import { MailBlock } from "../MailBlock";
import { SignBlock } from "../SignBlock";
import { NoteBlock } from "../NoteBlock";
import { RdvSheet } from "../RdvSheet";
import { GuideButton } from "../guide";
import { FicheShareLine } from "../share";
import { canonStage, nextStage, prevStage, stageList, stageOf, type SuiviDeal } from "../suivi";
import { RelanceBoard } from "../Relances";
import { ContactsButton, FeedButton, StatusPills, SuiviScope, useLive } from "../Suivi";
import type { RelationStatus } from "../suivi";
import type { CrmFiche } from "../types";

const TABS = [
  ["note", "Note"],
  ["coffre", "Coffre"],
  ["agenda", "Agenda"],
  ["affaire", "Affaire"],
  ["factures", "Factures"],
  ["mail", "Mail"],
  ["signature", "Signature"],
] as const;

type TabId = (typeof TABS)[number][0];

export function PhoneFiche({
  fiche,
  tone,
  editing,
  archived,
  onBack,
  onEdit,
  onSaved,
  onChange,
  onStatus,
  onArchive,
  onErase,
  onRestore,
  onNewTask,
  others = [],
  onRetired,
}: {
  fiche: CrmFiche;
  tone: RelationStatus;
  editing: boolean;
  archived: boolean;
  onBack: () => void;
  onEdit: () => void;
  onSaved: () => void;
  onChange: (fiche: CrmFiche) => void;
  onStatus: (status: RelationStatus) => void;
  onArchive: () => void;
  onErase: () => void;
  onRestore: () => void;
  onNewTask: () => void;
  others?: CrmFiche[];
  onRetired?: (fiche: CrmFiche, close?: boolean) => void;
}) {
  const [tab, setTab] = useState<TabId>("note");
  const [mails, setMails] = useState<string[]>(() => [fiche.email].filter((item) => item.trim() !== ""));

  useEffect(() => {
    void noteVisit(fiche.id).catch(() => undefined);
    if (aimedFind("Signature", fiche.id)) setTab("signature");
    else if (aimedFind("Devis", fiche.id) || aimedFind("Facture", fiche.id)) setTab("factures");
    else if (aimedFind("Kanban", fiche.id)) setTab("coffre");
    else if (aimedFind("Note", fiche.id)) setTab("note");
    let alive = true;
    setMails([fiche.email].filter((item) => item.trim() !== ""));
    void listFicheContacts(fiche.id).then((rows) => {
      if (!alive) return;
      setMails([...new Set([fiche.email, ...rows.map((row) => row.email)].map((item) => item.trim()).filter((item) => item.includes("@")))]);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [fiche.id, fiche.email]);

  if (editing) {
    return (
      <div className="crm-phone-main">
        <button type="button" className="mb-3 text-sm font-semibold crm-ink" onClick={onSaved}>Retour à la fiche</button>
        <FicheDesk fiche={fiche} others={others} onChange={onChange} onSaved={onSaved} onRetired={onRetired} />
      </div>
    );
  }

  const city = [fiche.postal_code, fiche.city].filter(Boolean).join(" ");
  const place = [fiche.address, city].filter(Boolean).join(", ");

  return (
    <SuiviScope ficheId={fiche.id} onStatus={onStatus}>
      <div className="crm-phone-main">
        <div className="mb-3 flex items-start gap-3">
          <button type="button" className="crm-pill grid h-10 w-10 shrink-0 place-items-center rounded-full text-lg" aria-label="Retour" onClick={onBack}>‹</button>
          <FicheMark path={fiche.logo_path} name={fiche.name} tone={tone} className="h-12 w-12 text-sm" />
          <div className="min-w-0 flex-1">
            <h1 className="crm-title truncate text-lg font-semibold tracking-tight">{fiche.name}</h1>
            <p className="truncate text-xs font-semibold tracking-[0.12em] text-slate-400">{fiche.code}</p>
            <p className="truncate text-xs text-slate-500">{identityLine(fiche)}</p>
          </div>
          <div className="flex shrink-0 gap-1">
            <GuideButton />
            {fiche.mine ? <>
            <button type="button" className="crm-phone-icon crm-pill" aria-label="Modifier la fiche" onClick={onEdit}>
              <span className="material-icons-round text-[20px] leading-none">edit</span>
            </button>
            {archived ? (
              <>
                <button type="button" className="crm-phone-icon crm-pill is-danger" aria-label="Supprimer la fiche" onClick={onErase}>
                  <span className="material-icons-round text-[20px] leading-none">delete</span>
                </button>
                <button type="button" className="crm-phone-icon crm-pill" aria-label="Réactiver la fiche" onClick={onRestore}>
                  <span className="material-icons-round text-[20px] leading-none">restore</span>
                </button>
              </>
            ) : (
              <button type="button" className="crm-phone-icon crm-pill" aria-label="Archiver la fiche" onClick={onArchive}>
                <span className="material-icons-round text-[20px] leading-none">archive</span>
              </button>
            )}
            </> : null}
          </div>
        </div>
        {place ? <p className="text-sm text-slate-600">{place}</p> : null}
        <p className="text-sm text-slate-600">{[fiche.email, fiche.phone].filter(Boolean).join(" · ") || "Aucun contact"}</p>
        <div className="crm-phone-pills mt-3">
          <StatusPills />
        </div>
        <div className="crm-phone-actions mt-3">
          <FeedButton folder={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} />
          <ContactsButton fiche={fiche} />
          <button type="button" className="crm-pill rounded-full text-xs font-semibold" onClick={onNewTask}>Tâche</button>
        </div>
        <div className="mt-3">
          <FicheShareLine fiche={fiche} onChange={onChange} />
        </div>
        <div className="crm-phone-tabs" role="tablist">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? "crm-fill rounded-full px-3 py-1.5 text-xs font-semibold text-white" : "crm-pill rounded-full px-3 py-1.5 text-xs font-semibold"} onClick={() => setTab(id)}>
              {label}
            </button>
          ))}
        </div>
        <div className="mt-3">
          {tab === "note" ? <NoteBlock key={fiche.id} folder={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} ficheId={fiche.id} origin={{ id: fiche.id, name: fiche.name, logo: fiche.logo_path }} /> : null}
          {tab === "coffre" ? <PhoneCoffre fiche={fiche} /> : null}
          {tab === "agenda" ? <PhoneAgenda fiche={fiche} /> : null}
          {tab === "affaire" ? <><PhoneDeal /><div className="mt-3"><RelanceBoard /></div></> : null}
          {tab === "factures" ? <PhoneFactures fiche={fiche} onChange={onChange} /> : null}
          {tab === "mail" ? <PhoneMail emails={mails} /> : null}
          {tab === "signature" ? <PhoneSign fiche={fiche} /> : null}
        </div>
      </div>
    </SuiviScope>
  );
}

function PhoneCoffre({ fiche }: { fiche: CrmFiche }) {
  const coffreOn = fiche.coffre_path !== "";
  const [atelier, setAtelier] = useState<AtelierStart | null>(null);

  return (
    <section className="crm-card overflow-hidden rounded-2xl border border-slate-200">
      <div className="crm-head flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <h2 className="min-w-0 truncate text-sm font-semibold">Coffre</h2>
        <span className="min-w-0 flex-1 truncate text-xs text-slate-500">{fiche.coffre_path}</span>
        <CoffreCreateButtons disabled={!coffreOn} onCreate={setAtelier} />
      </div>
      <div className="crm-phone-coffre p-3">
        {fiche.coffre_path ? <CoffreBlock home={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} fiche={fiche} tapOpens /> : <p className="text-sm text-slate-500">Le dossier de la fiche dans CRM clients n’est pas prêt.</p>}
      </div>
      {atelier && fiche.coffre_path ? <Atelier key={atelier} fiche={fiche} start={atelier} onClose={() => setAtelier(null)} /> : null}
    </section>
  );
}

function PhoneAgenda({ fiche }: { fiche: CrmFiche }) {
  const [marks, setMarks] = useState<AgendaMark[]>([]);
  const [cursor, setCursor] = useState(() => new Date());
  const [picked, setPicked] = useState(todayStamp);
  const [rdvOpen, setRdvOpen] = useState(false);
  const [rdvError, setRdvError] = useState("");
  const month = new Date(cursor.getFullYear(), cursor.getMonth(), 1);

  useEffect(() => {
    let alive = true;
    void listAllEvents().then((rows) => { if (alive) setMarks(rows); }).catch(() => undefined);
    return () => { alive = false; };
  }, [fiche.id]);

  const dayEvents = marks.filter((item) => item.day === picked).sort((a, b) => a.time.localeCompare(b.time));

  return (
    <section className="crm-card rounded-2xl border border-slate-200 p-3">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-sm font-semibold">Agenda</h2>
      </div>
      <div className="mb-2 flex items-center justify-between">
        <button type="button" className="grid h-9 w-9 place-items-center rounded-full text-lg" aria-label="Mois précédent" onClick={() => setCursor(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>‹</button>
        <p className="text-sm font-semibold capitalize">{month.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}</p>
        <button type="button" className="grid h-9 w-9 place-items-center rounded-full text-lg" aria-label="Mois suivant" onClick={() => setCursor(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>›</button>
      </div>
      <PhoneMonth month={month} marks={marks} picked={picked} onPick={setPicked} />
      <h3 className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{picked ? dayLabel(picked) : "Jour"}</h3>
      {dayEvents.length === 0 ? <p className="mt-2 text-sm text-slate-500">Rien ce jour.</p> : null}
      <ul className="mt-2 space-y-2">
        {dayEvents.map((item) => (
          <li key={item.id} className="flex items-start gap-2 rounded-xl border border-slate-200 px-3 py-2">
            <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.color }} />
            <span className="min-w-0">
              <span className="block text-xs font-semibold">{item.time || "Journée"}</span>
              <span className="block text-sm">{item.title}</span>
            </span>
          </li>
        ))}
      </ul>
      <button type="button" className="crm-pill mt-3 w-full rounded-full px-3 py-2 text-xs font-semibold" onClick={() => setRdvOpen(true)}>
        Proposer un RDV
      </button>
      {rdvError ? <p className="mt-2 text-center text-xs text-rose-700">{rdvError}</p> : null}
      {rdvOpen ? <RdvSheet fiche={fiche} onClose={() => setRdvOpen(false)} onError={setRdvError} /> : null}
    </section>
  );
}

function PhoneMonth({ month, marks, picked, onPick }: { month: Date; marks: AgendaMark[]; picked: string; onPick: (day: string) => void }) {
  const year = month.getFullYear();
  const index = month.getMonth();
  const today = new Date();
  const lead = (new Date(year, index, 1).getDay() + 6) % 7;
  const days = new Date(year, index + 1, 0).getDate();
  const cells = [...Array(lead).fill(0), ...Array.from({ length: days }, (_, i) => i + 1)];
  const labels = ["L", "M", "M", "J", "V", "S", "D"];

  return (
    <div className="crm-phone-month text-xs">
      {labels.map((label, i) => <b key={`${label}-${i}`} className="crm-ink py-1 text-[11px]">{label}</b>)}
      {cells.map((day, i) => {
        const stamp = day > 0 ? `${year}-${String(index + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : "";
        const on = stamp !== "" && today.getFullYear() === year && today.getMonth() === index && today.getDate() === day;
        const chosen = stamp !== "" && stamp === picked;
        const dot = marks.some((item) => item.day === stamp);
        return (
          <button key={i} type="button" disabled={!stamp} className="grid h-9 place-items-center disabled:cursor-default" onClick={() => stamp && onPick(stamp)}>
            <span className={chosen ? "crm-soft relative grid h-8 w-8 place-items-center rounded-full font-semibold" : on ? "crm-fill relative grid h-8 w-8 place-items-center rounded-full font-semibold text-white" : "relative grid h-8 w-8 place-items-center"}>
              {day || ""}
              {dot ? <span className="absolute bottom-0.5 h-1 w-1 rounded-full bg-current" /> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function PhoneDeal() {
  const { suivi, error, run } = useLive();
  const [moving, setMoving] = useState(false);
  const [deal, setDeal] = useState<SuiviDeal | null>(null);
  const stages = stageList(suivi?.stages);
  useEffect(() => {
    const raw = suivi?.deal;
    setDeal(raw ? { ...raw, status: canonStage(raw.status, stageList(suivi?.stages)) } : null);
  }, [suivi?.deal, suivi?.stages]);
  const stage = deal ? stageOf(canonStage(deal.status, stages), stages) : undefined;
  const next = deal ? nextStage(deal.status, stages) : null;
  const prev = deal ? prevStage(deal.status, stages) : null;
  const nextLabel = stageOf(next || "", stages)?.label || "";
  const prevLabel = stageOf(prev || "", stages)?.label || "";
  const lost = stages.find((item) => item.kind === "lost");

  async function commit(nextDeal: SuiviDeal) {
    setDeal(nextDeal);
    await run({ action: "deal", ...nextDeal, quote_label: "" });
  }

  async function move(status: string) {
    if (!deal || moving) return;
    setMoving(true);
    try {
      await commit({ ...deal, status });
    } finally {
      setMoving(false);
    }
  }

  if (!suivi) return <p className="text-sm text-slate-500">Chargement de l’affaire…</p>;
  if (!deal) return <p className="text-sm text-slate-500">Aucune affaire sur cette fiche.</p>;

  return (
    <section className="crm-card rounded-2xl border border-slate-200 p-4" style={{ boxShadow: `inset 0 3px 0 ${stage?.color || "#94a3b8"}` }}>
      <p className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: stage?.color }}>{stage?.label}</p>
      <p className="mt-2 text-base font-semibold">{deal.title.trim() || "Sans intitulé"}</p>
      <p className="mt-1 text-sm text-slate-600">{deal.amount > 0 ? euros(deal.amount) : "Sans montant"}</p>
      <div className="mt-3 grid gap-2">
        <input className="crm-field h-10 rounded-xl border px-3 text-sm" type="number" min="0" max="100" step="1" value={deal.probability || ""} placeholder="Probabilité %" onChange={(event) => setDeal({ ...deal, probability: Math.max(0, Math.min(100, Number(event.target.value) || 0)) })} onBlur={() => { if (suivi && deal.probability !== suivi.deal.probability) void commit(deal); }} />
        <input className="crm-field h-10 rounded-xl border px-3 text-sm" type="date" value={deal.expected_on || ""} aria-label="Date de clôture prévue" onChange={(event) => setDeal({ ...deal, expected_on: event.target.value })} onBlur={() => { if (suivi && deal.expected_on !== suivi.deal.expected_on) void commit(deal); }} />
        {stage?.kind === "lost" ? <input className="crm-field h-10 rounded-xl border px-3 text-sm" value={deal.loss_reason || ""} placeholder="Motif de perte" onChange={(event) => setDeal({ ...deal, loss_reason: event.target.value })} onBlur={() => { if (suivi && deal.loss_reason !== suivi.deal.loss_reason) void commit(deal); }} /> : null}
      </div>
      {error ? <p className="mt-2 text-sm text-rose-700">{error}</p> : null}
      <div className="mt-4 flex flex-col gap-2">
        {next ? <button type="button" className="rounded-full crm-fill px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" disabled={moving} onClick={() => void move(next)}>→ {nextLabel}</button> : null}
        {stage?.kind === "open" && lost ? <button type="button" className="crm-pill rounded-full px-3 py-2 text-sm font-semibold disabled:opacity-50" disabled={moving} onClick={() => void move(lost.code)}>→ {lost.label}</button> : null}
        {prev ? <button type="button" className="crm-pill rounded-full px-3 py-2 text-sm font-semibold disabled:opacity-50" disabled={moving} onClick={() => void move(prev)}>← {prevLabel}</button> : null}
      </div>
      {(suivi.history || []).length > 0 ? (
        <ul className="mt-4 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-500">
          {suivi.history.map((item) => (
            <li key={item.id} className="truncate">{[item.title.trim() || "Sans intitulé", item.amount > 0 ? euros(item.amount) : ""].filter(Boolean).join(" · ")}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function PhoneFactures({ fiche, onChange }: { fiche: CrmFiche; onChange: (fiche: CrmFiche) => void }) {
  const linked = fiche.facturx_client_id > 0;
  return (
    <section className="space-y-3">
      {linked ? <FactureBoard fiche={fiche} /> : <FactureTab fiche={fiche} onChange={onChange} onLinked={() => undefined} />}
    </section>
  );
}

function PhoneMail({ emails }: { emails: string[] }) {
  return (
    <section className="crm-card rounded-2xl border border-slate-200 p-3">
      <h2 className="mb-2 text-sm font-semibold">Mail</h2>
      {emails.length === 0 ? <p className="text-sm text-slate-500">Pas d’e-mail sur la fiche.</p> : <MailBlock emails={emails} />}
    </section>
  );
}

function PhoneSign({ fiche }: { fiche: CrmFiche }) {
  return (
    <section className="crm-card rounded-2xl border border-slate-200 p-3">
      <h2 className="mb-2 text-sm font-semibold">Signature</h2>
      <SignBlock folderId={fiche.sign_folder_id ?? 0} companyId={fiche.facturx_company_id} clientId={fiche.facturx_client_id} email={fiche.email} phone={fiche.phone} ficheName={fiche.name} ficheId={fiche.id} />
    </section>
  );
}

function euros(value: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function todayStamp(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function dayLabel(stamp: string): string {
  const [year, month, day] = stamp.split("-").map(Number);
  if (!year || !month || !day) return "";
  return new Date(year, month - 1, day).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
}
